import React, { useEffect, useRef, useState } from "react";
import { X, Loader, Ticket, Lock, Check, Wallet, Heart } from "lucide-react";
import Hls from "hls.js";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

/**
 * TvPlayer — full-screen video player for the Smart TV app.
 *
 * - Uses hls.js for .m3u8 sources in browsers that need MSE (LG, Fire TV).
 *   Safari / native HLS is used directly when available.
 * - Falls back to a plain <video src> for MP4/WebM and to a fullscreen <iframe>
 *   for external embeds (YouTube / Vimeo / etc.).
 * - Retries playback once on error (weak-TV-browser recovery).
 * - PPV: if the video is `is_ppv` and the user hasn't unlocked it, the player
 *   plays a free preview (per-video duration) then pauses and surfaces a
 *   paywall overlay with a Stripe checkout CTA. Already-paid users skip the
 *   gate entirely (server access check in TvApp pre-fetches; we double-check
 *   on mount in case of race conditions).
 * - Closes on Back/Escape via the TvApp spatial-nav hook.
 */
export default function TvPlayer({ video, onClose }) {
  const vref = useRef(null);
  const hlsRef = useRef(null);
  const [state, setState] = useState("loading"); // loading | ready | error
  const [retries, setRetries] = useState(0);
  const { user } = useAuth();

  // PPV state — `unlocked` mirrors the access check; "paywall" overlay shown
  // once preview seconds elapse OR if no playable src + ppv flag.
  const isPpv = !!video?.is_ppv && Number(video?.ppv_price_usd) > 0;
  const previewSec = Math.max(0, Number(video?.ppv_preview_seconds || 30));
  const [unlocked, setUnlocked] = useState(!isPpv || !!video?.ppv_access);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [previewLeft, setPreviewLeft] = useState(previewSec);
  const [busy, setBusy] = useState(false);
  const [ppvErr, setPpvErr] = useState("");
  // Wallet state — fetched lazily so the paywall can show "Pay with wallet"
  // when the viewer has enough balance, and so tip buttons are interactive.
  const [wallet, setWallet] = useState(null);
  const [tipBusy, setTipBusy] = useState(0);
  const [tipMsg, setTipMsg] = useState("");

  // Lazy-load wallet when paywall opens or when an unlocked video has a creator
  // (so tip buttons appear even on free titles).
  useEffect(() => {
    if (!user) return;
    if (!paywallOpen && unlocked && !video?.creator_id) return;
    api.get("/funds/me").then((r) => setWallet(r.data || null)).catch(() => setWallet(null));
  }, [paywallOpen, unlocked, user, video?.creator_id]);

  // Verify access on mount when PPV — guards against stale `ppv_access` from list.
  useEffect(() => {
    if (!isPpv) { setUnlocked(true); return; }
    if (video?.ppv_access) { setUnlocked(true); return; }
    if (!user || !video?.id) { setUnlocked(false); return; }
    let cancelled = false;
    api.get(`/videos/${video.id}/ppv/access`)
      .then((r) => { if (!cancelled) setUnlocked(!!r.data?.has_access); })
      .catch(() => { if (!cancelled) setUnlocked(false); });
    return () => { cancelled = true; };
  }, [isPpv, video?.id, video?.ppv_access, user]);

  useEffect(() => {
    if (!video) return;
    if (video.is_external && video.embed_url) {
      setState("ready");
      return;
    }
    // Live in-app WebRTC streams (provider=simulated with no HLS URL) are
    // played via an iframe pointing at /live/{id} — the existing React
    // <Watch> page already handles the WebRTC handshake. TV browsers
    // (Tizen, webOS, Fire) all support WebRTC; this saves us from porting
    // signaling logic into the TV player.
    if (video.kind === "live" && !video.src && video.id) {
      setState("ready");
      return;
    }
    const el = vref.current;
    if (!video.src && video.kind === "live") { setState("live_unavailable"); return; }
    if (!el || !video.src) { setState("error"); return; }

    const isHls = /\.m3u8($|\?)/i.test(video.src);
    let cancelled = false;

    async function play() {
      try {
        if (isHls && Hls.isSupported()) {
          const hls = new Hls({ maxBufferLength: 20, enableWorker: true });
          hls.loadSource(video.src);
          hls.attachMedia(el);
          hlsRef.current = hls;
          hls.on(Hls.Events.MANIFEST_PARSED, () => { if (!cancelled) { el.play().catch(() => {}); setState("ready"); } });
          hls.on(Hls.Events.ERROR, (_, data) => {
            if (data.fatal && retries < 1) {
              setRetries((r) => r + 1);
              try { hls.recoverMediaError(); } catch { setState("error"); }
            } else if (data.fatal) {
              setState("error");
            }
          });
        } else {
          el.src = video.src;
          el.play().then(() => setState("ready")).catch(() => setState("ready")); // show controls even if autoplay blocked
        }
      } catch {
        setState("error");
      }
    }

    play();

    // Resume at saved progress if provided (Continue Watching) — but only
    // for unlocked content. PPV previews always start from 0 so the
    // ppv_preview_seconds budget is honoured.
    if (video?.progress_sec && video.progress_sec > 5 && !video.is_external && unlocked) {
      const seek = () => {
        try { if (el && el.duration && !isNaN(el.duration)) el.currentTime = Math.min(video.progress_sec, el.duration - 3); } catch {}
        el?.removeEventListener("loadedmetadata", seek);
      };
      el?.addEventListener("loadedmetadata", seek);
    }

    // Save watch progress every 15s + on unmount (for Continue Watching)
    let progTimer = null;
    const saveProgress = () => {
      if (!video?.id || video.kind === "live" || !el) return;
      // Don't write progress for locked PPV previews — they'd poison
      // Continue Watching with 30s teasers users can't actually resume.
      if (isPpv && !unlocked) return;
      const p = Math.floor(el.currentTime || 0);
      const d = Math.floor(el.duration || 0);
      if (p < 5) return;
      try {
        api.post(`/history/${video.id}?progress_sec=${p}&duration_sec=${d}`).catch(() => {});
      } catch {}
    };
    progTimer = setInterval(saveProgress, 15000);

    return () => {
      cancelled = true;
      if (progTimer) { clearInterval(progTimer); saveProgress(); }
      try { hlsRef.current?.destroy(); } catch {}
      hlsRef.current = null;
      if (el) { el.pause(); el.removeAttribute("src"); el.load(); }
    };
  }, [video, retries, unlocked, isPpv]);

  // PPV preview ticker — pauses playback at previewSec and surfaces paywall.
  // We back the timeupdate listener with a wall-clock setTimeout so even if
  // autoplay is blocked (TV kiosks, mobile Safari) the paywall still opens
  // after the preview window elapses from the moment the player mounted.
  useEffect(() => {
    if (!isPpv || unlocked) return;
    const el = vref.current;
    let fallback = null;
    const onTime = () => {
      if (!el) return;
      const t = el.currentTime || 0;
      const left = Math.max(0, Math.ceil(previewSec - t));
      setPreviewLeft(left);
      if (t >= previewSec) {
        try { el.pause(); } catch {}
        setPaywallOpen(true);
      }
    };
    if (el) el.addEventListener("timeupdate", onTime);
    if (previewSec > 0) {
      fallback = setTimeout(() => {
        try { el?.pause(); } catch {}
        setPaywallOpen(true);
      }, previewSec * 1000);
    } else {
      // Zero-second preview — open paywall immediately.
      setPaywallOpen(true);
    }
    return () => {
      if (el) el.removeEventListener("timeupdate", onTime);
      if (fallback) clearTimeout(fallback);
    };
  }, [isPpv, unlocked, previewSec]);

  // If the video can't play at all (no src, error state, or external embed
  // for a PPV title) the user must STILL be able to buy a ticket. Force the
  // paywall open whenever the media is unusable for a locked PPV title — so
  // a busted upload or YouTube embed never accidentally hides the CTA.
  useEffect(() => {
    if (!isPpv || unlocked) return;
    if (state === "error" || state === "live_unavailable") {
      setPaywallOpen(true);
      return;
    }
    if (!video?.src && !(video?.is_external && video?.embed_url)) {
      setPaywallOpen(true);
    }
  }, [isPpv, unlocked, state, video]);

  const startCheckout = async () => {
    setPpvErr("");
    if (!user) { setPpvErr("Sign in on your phone or laptop to buy this PPV title, then this TV will unlock automatically."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/videos/${video.id}/ppv/checkout`, { origin_url: window.location.origin });
      if (data.already_purchased) {
        setUnlocked(true);
        setPaywallOpen(false);
        try { vref.current?.play(); } catch {}
        return;
      }
      // Smart-TV browsers can't reliably hand off to Stripe Checkout. Open
      // in a new tab when possible; otherwise navigate this tab so the
      // user finishes the purchase and bounces back.
      if (data?.url) {
        const w = window.open(data.url, "_blank", "noopener,noreferrer");
        if (!w) window.location.href = data.url;
      }
    } catch (e) {
      setPpvErr(e?.response?.data?.detail || "Couldn't start checkout. Try again from /watch on your phone.");
    } finally {
      setBusy(false);
    }
  };

  const payWithWallet = async () => {
    setPpvErr("");
    setBusy(true);
    try {
      const { data } = await api.post(`/funds/ppv-unlock/${video.id}`, {});
      if (data.already_purchased || data.ok) {
        setUnlocked(true);
        setPaywallOpen(false);
        try { vref.current?.play(); } catch {}
        if (typeof data.balance_usd === "number") {
          setWallet((w) => w ? { ...w, balance_usd: data.balance_usd } : { balance_usd: data.balance_usd });
        }
      }
    } catch (e) {
      setPpvErr(e?.response?.data?.detail || "Wallet payment failed. Add funds and try again.");
    } finally {
      setBusy(false);
    }
  };

  const sendTip = async (amount) => {
    if (!video?.creator_id) return;
    setPpvErr("");
    setTipBusy(amount);
    try {
      const { data } = await api.post(`/funds/tip`, {
        creator_id: video.creator_id,
        amount: Number(amount),
        message: tipMsg.trim() || undefined,
        video_id: video.id,
      });
      setWallet((w) => w ? { ...w, balance_usd: data.balance_usd } : { balance_usd: data.balance_usd });
      setTipMsg("");
    } catch (e) {
      setPpvErr(e?.response?.data?.detail || "Tip failed. Add funds first.");
    } finally {
      setTipBusy(0);
    }
  };

  const balanceUsd = Number(wallet?.balance_usd || 0);
  const priceUsd = Number(video?.ppv_price_usd || 0);
  const canPayWithWallet = balanceUsd >= priceUsd && priceUsd > 0;

  const isLiveInApp = video?.kind === "live" && !video?.src && !!video?.id;

  return (
    <div className="w-full h-full bg-black relative" data-testid="tv-player">
      {video?.is_external && video?.embed_url ? (
        <iframe
          src={toTvEmbed(video.embed_url)}
          title={video.title || "Video"}
          allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
          allowFullScreen
          className="w-full h-full border-0"
        />
      ) : isLiveInApp ? (
        // In-app WebRTC live: iframe the existing /live/{id} React page so
        // the TV browser handles the peer-connection itself. `?embed=tv`
        // hides nav/chat sidebar and locks the player full-bleed.
        <iframe
          data-testid="tv-player-live-iframe"
          src={`/live/${video.id}?embed=tv`}
          title={video.title || "Live stream"}
          allow="autoplay; camera; microphone; encrypted-media; picture-in-picture; fullscreen"
          allowFullScreen
          className="w-full h-full border-0"
        />
      ) : (
        <video
          ref={vref}
          controls
          autoPlay
          playsInline
          className="w-full h-full object-contain bg-black"
          onError={() => setState((s) => (s === "error" ? s : "error"))}
        />
      )}

      {/* PPV preview ribbon — visible while the user is sampling the free portion */}
      {isPpv && !unlocked && !paywallOpen && (
        <div data-testid="tv-ppv-preview-ribbon" className="absolute left-1/2 -translate-x-1/2 bg-black/80 backdrop-blur border border-tv-accent/60 text-white rounded-full px-4 py-1.5 inline-flex items-center gap-2 z-30 shadow-2xl"
          style={{ top: "calc(var(--tv-safe-y, 3vh) + 10px)" }}
        >
          <Ticket className="w-3.5 h-3.5 text-tv-accent" />
          <span className="text-[0.8em] font-display font-bold uppercase tracking-wider">Free preview</span>
          <span className="text-[0.85em] font-black tabular-nums">{previewLeft}s</span>
          <span className="text-white/55 text-[0.75em]">·</span>
          <span className="text-[0.75em] text-white/85">${Number(video?.ppv_price_usd || 0).toFixed(2)} to unlock</span>
        </div>
      )}

      {state === "loading" && !paywallOpen && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/80 gap-4">
          <Loader className="w-[2.5em] h-[2.5em] text-tv-accent animate-spin" />
          <div className="font-display font-bold text-[1.3em]">Loading…</div>
        </div>
      )}

      {state === "error" && !paywallOpen && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/90 gap-3 text-center" style={{ padding: "0 var(--tv-safe-x, 5vw)" }}>
          <div className="font-display font-black text-[2em]">Playback failed</div>
          <div className="text-white/65 text-[1.05em] max-w-[60vw] leading-snug">
            This title doesn't have a TV-ready video source — it's likely an image-only story or short.
          </div>
          <div className="text-white/45 text-[0.9em] max-w-[60vw] leading-snug">
            Try another title, or watch the full version on your phone or computer.
          </div>
        </div>
      )}

      {state === "live_unavailable" && !paywallOpen && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/90 gap-4 text-center" style={{ padding: "0 var(--tv-safe-x, 5vw)" }}>
          <div className="font-display font-black text-[2em]">Live stream not available on TV</div>
          <div className="text-white/70 text-[1.1em] max-w-[60vw] leading-snug">
            This broadcaster is using the <strong>in-app camera</strong> (peer-to-peer) — your TV can't join that signaling channel.
          </div>
          <div className="text-white/55 text-[0.95em] max-w-[60vw] leading-snug">
            Watch it on your phone or computer at <span className="text-tv-accent">tapintvnetwork.com/live/{video?.id?.slice(0, 8)}…</span>, or ask the host to broadcast via OBS → Cloudflare RTMP for a TV-friendly HLS feed.
          </div>
        </div>
      )}

      {/* PPV paywall — shown after the preview window elapses */}
      {paywallOpen && isPpv && !unlocked && (
        <div data-testid="tv-ppv-paywall" className="absolute inset-0 z-40 flex flex-col items-center justify-center bg-black/85 backdrop-blur-md text-center" style={{ padding: "0 var(--tv-safe-x, 5vw)" }}>
          {video?.thumbnail_url && (
            <img src={video.thumbnail_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/85 to-black" />
          <div className="relative z-10 max-w-[60vw]">
            <div className="text-[0.85em] uppercase tracking-[0.3em] text-tv-accent font-bold inline-flex items-center gap-2 justify-center">
              <Ticket className="w-4 h-4" /> Pay-Per-View
            </div>
            <h2 className="font-display font-black text-[2.6em] leading-tight mt-2">
              Preview's over — unlock the full show
            </h2>
            <p className="text-white/75 text-[1.1em] mt-3 max-w-[42em] mx-auto leading-snug">
              You watched the free {previewSec}s sample of <strong>{video?.title}</strong>. One ticket = unlimited replay in HD. Revenue is split with the creator.
            </p>
            {ppvErr && <div className="text-rose-300 text-[0.95em] mt-3">{ppvErr}</div>}
            <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
              {canPayWithWallet && (
                <button
                  data-tv-focus="true"
                  data-testid="tv-ppv-pay-wallet-btn"
                  disabled={busy}
                  onClick={payWithWallet}
                  autoFocus
                  className="tv-focusable inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-black font-display font-black text-[1.15em] px-[1.6em] py-[0.85em] rounded-full disabled:opacity-60"
                >
                  <Wallet className="w-[1.1em] h-[1.1em]" />
                  Pay ${priceUsd.toFixed(2)} with wallet
                </button>
              )}
              <button
                data-tv-focus="true"
                data-testid="tv-ppv-buy-btn"
                disabled={busy}
                onClick={startCheckout}
                autoFocus={!canPayWithWallet}
                className="tv-focusable inline-flex items-center gap-2 bg-tv-accent hover:bg-yellow-300 text-black font-display font-black text-[1.15em] px-[1.6em] py-[0.85em] rounded-full disabled:opacity-60"
              >
                <Lock className="w-[1.1em] h-[1.1em]" />
                {busy ? "Opening checkout…" : `Unlock for $${priceUsd.toFixed(2)}`}
              </button>
              <button
                data-tv-focus="true"
                data-testid="tv-ppv-replay-preview"
                onClick={() => {
                  try {
                    if (vref.current) {
                      vref.current.currentTime = 0;
                      vref.current.play().catch(() => {});
                    }
                  } catch {}
                  setPaywallOpen(false);
                }}
                className="tv-focusable inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-display font-bold text-[1em] px-[1.4em] py-[0.85em] rounded-full"
              >
                Replay preview
              </button>
              <button
                data-tv-focus="true"
                data-testid="tv-ppv-close-btn"
                onClick={onClose}
                className="tv-focusable inline-flex items-center gap-2 bg-transparent border border-white/30 text-white font-display font-bold text-[1em] px-[1.4em] py-[0.85em] rounded-full hover:bg-white/10"
              >
                Back
              </button>
            </div>

            {/* Tip the host — micro-payments from wallet */}
            {video?.creator_id && (
              <div data-testid="tv-ppv-tip-section" className="mt-6 border-t border-white/10 pt-5">
                <div className="text-[0.7em] uppercase tracking-[0.3em] text-white/55 font-bold inline-flex items-center gap-2 justify-center">
                  <Heart className="w-3.5 h-3.5 text-rose-400" /> Or tip the host
                </div>
                <div className="text-white/60 text-[0.85em] mt-1">
                  Wallet: <strong className="text-white">${balanceUsd.toFixed(2)}</strong>
                  {balanceUsd < 1 && <> · <a href="/funds" target="_blank" rel="noreferrer" className="underline text-tv-accent">Add funds on phone</a></>}
                </div>
                <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
                  {[1, 5, 10, 25].map((amt) => (
                    <button
                      key={amt}
                      data-tv-focus="true"
                      data-testid={`tv-ppv-tip-btn-${amt}`}
                      disabled={tipBusy === amt || balanceUsd < amt}
                      onClick={() => sendTip(amt)}
                      className="tv-focusable inline-flex items-center gap-1 bg-rose-500/15 hover:bg-rose-500/30 disabled:opacity-40 text-rose-200 hover:text-white font-bold text-[0.95em] px-3 py-1.5 rounded-full border border-rose-500/30"
                    >
                      <Heart className="w-3 h-3" /> ${amt}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="text-white/45 text-[0.85em] mt-5">
              Already bought it? <button
                onClick={async () => {
                  try {
                    const r = await api.get(`/videos/${video.id}/ppv/access`);
                    if (r.data?.has_access) {
                      setUnlocked(true);
                      setPaywallOpen(false);
                      try { vref.current?.play(); } catch {}
                    } else {
                      setPpvErr("No purchase found yet — finish checkout in the new tab and tap Refresh.");
                    }
                  } catch { setPpvErr("Couldn't refresh access. Check your connection."); }
                }}
                data-testid="tv-ppv-refresh-access"
                className="underline hover:text-white"
              >Refresh access</button>
            </div>
          </div>
        </div>
      )}

      {/* "Unlocked" toast for paid users — small confirmation, auto-fades */}
      {isPpv && unlocked && state === "ready" && (
        <div data-testid="tv-ppv-unlocked-badge" className="absolute right-1/2 translate-x-1/2 bg-emerald-500/90 text-black rounded-full px-3 py-1 inline-flex items-center gap-1.5 z-30 text-[0.8em] font-display font-black uppercase tracking-wider"
          style={{ top: "calc(var(--tv-safe-y, 3vh) + 10px)", animation: "tvFadeOut 4.5s ease forwards" }}
        >
          <Check className="w-3.5 h-3.5" /> PPV unlocked
        </div>
      )}

      <button
        data-tv-focus="true"
        data-testid="tv-player-close"
        onClick={onClose}
        autoFocus={!paywallOpen}
        className="tv-focusable absolute rounded-full bg-black/70 hover:bg-black/90 border border-white/20 flex items-center justify-center z-30"
        style={{ top: "var(--tv-safe-y, 3vh)", right: "var(--tv-safe-x, 5vw)", width: "clamp(48px, 4vw, 120px)", height: "clamp(48px, 4vw, 120px)" }}
        aria-label="Close"
      >
        <X className="w-[48%] h-[48%]" />
      </button>

      <div
        className="absolute text-white font-display font-black drop-shadow-lg pointer-events-none"
        style={{ bottom: "var(--tv-safe-y, 3vh)", left: "var(--tv-safe-x, 5vw)", right: "var(--tv-safe-x, 5vw)", fontSize: "clamp(20px, 2vw, 60px)" }}
      >
        {video?.title}
      </div>

      <style>{`
        @keyframes tvFadeOut {
          0% { opacity: 0; transform: translate(-50%, -8px); }
          15% { opacity: 1; transform: translate(-50%, 0); }
          70% { opacity: 1; }
          100% { opacity: 0; transform: translate(-50%, -8px); pointer-events: none; }
        }
      `}</style>
    </div>
  );
}

function toTvEmbed(url) {
  if (!url) return url;
  // YouTube: autoplay + tv-friendly params
  if (/youtube\.com|youtu\.be/.test(url)) {
    const sep = url.includes("?") ? "&" : "?";
    return `${url}${sep}autoplay=1&rel=0&modestbranding=1`;
  }
  return url;
}
